import type { User } from '../types'

export type UserType = 'BUYER' | 'VENDOR' | 'ENTERPRISE_BUYER' | 'ENTERPRISE_VENDOR' | 'ADMIN' | 'AGENCY' | 'GUEST'

export interface UserPermissions {
  // Navigation permissions
  canAccessMarketplace: boolean
  canAccessAuctions: boolean
  canSearchProducts: boolean
  canAccessEnterprise: boolean
  canAccessROICalculator: boolean
  canAccessCategories: boolean
  
  // Dashboard permissions
  canViewMyListings: boolean
  canViewMyBids: boolean
  canViewMyOrders: boolean
  canViewMessages: boolean
  canViewWallet: boolean
  canViewAnalytics: boolean
  canViewQuotes: boolean
  
  // Action permissions
  canCreateListing: boolean
  canPlaceBid: boolean
  canBuyNow: boolean
  canSell: boolean
  canRequestQuote: boolean
  canManageServices: boolean
  
  // UI element permissions
  showShoppingCart: boolean
  showSellButton: boolean
  showWishlistButton: boolean
  showBiddingFeatures: boolean
  showQuoteFeatures: boolean
}

export function getUserType(user: User | null): UserType {
  if (!user) return 'GUEST'
  
  if (user.role === 'ADMIN') return 'ADMIN'
  
  // Government Agency
  if (user.role === 'AGENCY') return 'AGENCY'
  
  // REFACTORED: Standardized Enterprise User Detection
  // Using consistent logic: role + isEnterprise flag instead of ENTERPRISE_BUYER enum
  
  // Enterprise Buyer: BUYER role with enterprise flag
  if (user.role === 'BUYER' && user.isEnterprise === true) {
    return 'ENTERPRISE_BUYER'
  }
  
  // Enterprise Vendor: VENDOR role with enterprise flag  
  if (user.role === 'VENDOR' && user.isEnterprise === true) {
    return 'ENTERPRISE_VENDOR'
  }
  
  // BACKWARD COMPATIBILITY: Handle legacy direct role assignments
  // These checks remain for any existing data that may still use direct role assignments
  if (user.role === 'ENTERPRISE_BUYER') return 'ENTERPRISE_BUYER'
  if (user.role === 'ENTERPRISE_VENDOR') return 'ENTERPRISE_VENDOR'
  
  // Regular Vendor
  if (user.role === 'VENDOR') {
    return 'VENDOR'
  }
  
  // Regular Buyer
  return 'BUYER'
}

export function getUserPermissions(user: User | null): UserPermissions {
  const userType = getUserType(user)
  const isApproved = isEnterpriseApproved(user)
  
  switch (userType) {
    case 'AGENCY':
      return {
        // Navigation - Only access to government-specific features
        canAccessMarketplace: false,
        canAccessAuctions: false,
        canSearchProducts: false,
        canAccessEnterprise: false,
        canAccessROICalculator: false,
        canAccessCategories: false,
        
        // Dashboard - Minimal permissions
        canViewMyListings: false,
        canViewMyBids: false,
        canViewMyOrders: false,
        canViewMessages: true, // Can view messages for RFQ communication
        canViewWallet: false,
        canViewAnalytics: false,
        canViewQuotes: false,
        
        // Actions - Only RFQ management
        canCreateListing: false,
        canPlaceBid: false,
        canBuyNow: false,
        canSell: false,
        canRequestQuote: false,
        canManageServices: false,
        
        // UI elements - No marketplace features
        showShoppingCart: false,
        showSellButton: false,
        showWishlistButton: false,
        showBiddingFeatures: false,
        showQuoteFeatures: false
      }
      
    case 'ENTERPRISE_VENDOR':
      return {
        // Navigation
        canAccessMarketplace: false,
        canAccessAuctions: false,
        canSearchProducts: true,
        canAccessEnterprise: false, // Vendor directory is exclusively for enterprise buyers
        canAccessROICalculator: false, // ROI Calculator is exclusively for buyers
        canAccessCategories: false,
        
        // Dashboard
        canViewMyListings: true, // Enterprise vendors can view their services/listings
        canViewMyBids: false,
        canViewMyOrders: true, // Enterprise vendors can view their orders/contracts
        canViewMessages: true, // Can always view messages
        canViewWallet: true, // Enterprise vendors can view wallet
        canViewAnalytics: isApproved,
        canViewQuotes: true, // Quote management for enterprise vendors - ALWAYS ENABLED
        
        
        // Actions
        canCreateListing: isApproved, // Create services - only if approved
        canPlaceBid: false,
        canBuyNow: false,
        canSell: isApproved, // Can only sell if approved
        canRequestQuote: false,
        canManageServices: isApproved, // Service management only if approved
        
        // UI elements
        showShoppingCart: false,
        showSellButton: true, // Enterprise vendors can sell services
        showWishlistButton: false,
        showBiddingFeatures: false,
        showQuoteFeatures: true // Quote features - ALWAYS ENABLED for enterprise vendors
      }
      
    case 'ENTERPRISE_BUYER':
      return {
        // Navigation
        canAccessMarketplace: false,
        canAccessAuctions: false,
        canSearchProducts: true,
        canAccessEnterprise: true, // Enterprise buyers can access vendor directory
        canAccessROICalculator: true, // ROI Calculator available for all enterprise buyers
        canAccessCategories: false,
        
        // Dashboard
        canViewMyListings: false,
        canViewMyBids: false,
        canViewMyOrders: true, // Enterprise buyers can view their orders/contracts
        canViewMessages: true, // Can always view messages
        canViewWallet: true, // Enterprise buyers can view wallet
        canViewAnalytics: isApproved,
        canViewQuotes: true, // Quote requests for enterprise buyers - ALWAYS ENABLED
        
        // Actions
        canCreateListing: false,
        canPlaceBid: false,
        canBuyNow: false,
        canSell: false,
        canRequestQuote: true, // Quote requests - ALWAYS ENABLED for enterprise buyers
        canManageServices: false,
        
        // UI elements
        showShoppingCart: false,
        showSellButton: false,
        showWishlistButton: false,
        showBiddingFeatures: false,
        showQuoteFeatures: true // Quote features - ALWAYS ENABLED for enterprise buyers
      }
      
    case 'VENDOR':
      return {
        // Navigation
        canAccessMarketplace: true,
        canAccessAuctions: true,
        canSearchProducts: true,
        canAccessEnterprise: false,
        canAccessROICalculator: false,
        canAccessCategories: true,
        
        // Dashboard
        canViewMyListings: true,
        canViewMyBids: true,
        canViewMyOrders: true,
        canViewMessages: true,
        canViewWallet: true,
        canViewAnalytics: false,
        canViewQuotes: false,
        
        // Actions
        canCreateListing: true,
        canPlaceBid: true,
        canBuyNow: true,
        canSell: true,
        canRequestQuote: false,
        canManageServices: false,
        
        // UI elements
        showShoppingCart: true,
        showSellButton: true,
        showWishlistButton: true,
        showBiddingFeatures: true,
        showQuoteFeatures: false
      }
      
    case 'BUYER':
      return {
        // Navigation
        canAccessMarketplace: true,
        canAccessAuctions: true,
        canSearchProducts: true,
        canAccessEnterprise: false,
        canAccessROICalculator: false,
        canAccessCategories: true,
        
        // Dashboard
        canViewMyListings: false,
        canViewMyBids: true,
        canViewMyOrders: true,
        canViewMessages: true,
        canViewWallet: true,
        canViewAnalytics: false,
        canViewQuotes: false,
        
        // Actions
        canCreateListing: false,
        canPlaceBid: true,
        canBuyNow: true,
        canSell: false,
        canRequestQuote: false,
        canManageServices: false,
        
        // UI elements
        showShoppingCart: true,
        showSellButton: false,
        showWishlistButton: true,
        showBiddingFeatures: true,
        showQuoteFeatures: false
      }
      
    case 'ADMIN':
      // Admin is for setup/config only - not part of Evoltbay user flow. Hide marketplace nav and shopping features.
      return {
        // Navigation - No marketplace/auction/categories/enterprise links for admin
        canAccessMarketplace: false,
        canAccessAuctions: false,
        canSearchProducts: false,
        canAccessEnterprise: false,
        canAccessROICalculator: false,
        canAccessCategories: false,
        
        // Dashboard
        canViewMyListings: true,
        canViewMyBids: true,
        canViewMyOrders: true,
        canViewMessages: true,
        canViewWallet: true,
        canViewAnalytics: true,
        canViewQuotes: true,
        
        // Actions
        canCreateListing: true,
        canPlaceBid: true,
        canBuyNow: true,
        canSell: true,
        canRequestQuote: true,
        canManageServices: true,
        
        // UI elements - No cart, wishlist, or sell for admin
        showShoppingCart: false,
        showSellButton: false,
        showWishlistButton: false,
        showBiddingFeatures: true,
        showQuoteFeatures: true
      }
      
    default: // GUEST
      return {
        // Navigation
        canAccessMarketplace: true,
        canAccessAuctions: true,
        canSearchProducts: true,
        canAccessEnterprise: false,
        canAccessROICalculator: false,
        canAccessCategories: true,
        
        // Dashboard
        canViewMyListings: false,
        canViewMyBids: false,
        canViewMyOrders: false,
        canViewMessages: false,
        canViewWallet: false,
        canViewAnalytics: false,
        canViewQuotes: false,
        
        // Actions
        canCreateListing: false,
        canPlaceBid: false,
        canBuyNow: false,
        canSell: false,
        canRequestQuote: false,
        canManageServices: false,
        
        // UI elements
        showShoppingCart: false,
        showSellButton: false,
        showWishlistButton: false,
        showBiddingFeatures: false,
        showQuoteFeatures: false
      }
  }
}

export function isEnterpriseUser(user: User | null): boolean {
  if (!user) return false
  return user.isEnterprise === true
}

export function isEnterpriseApproved(user: User | null): boolean {
  if (!user || !user.isEnterprise) return false
  return user.verified === true
}

export function shouldShowFeature(user: User | null, feature: keyof UserPermissions): boolean {
  const permissions = getUserPermissions(user)
  return permissions[feature]
} 